import { reflect } from '@effector/reflect';
import { NumberInput, Select } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { viewerModel } from 'entities/viewer';
import { JACKPOT_TYPES, JackpotType, USER_ROLES } from 'shared/api';
import { $t, translated } from 'shared/lib';
import { createModalForm } from 'shared/ui';
import { AddJackpotModel } from '../../model';
import cls from './add-jackpot-modal.module.scss';

export const createAddJackpotModal = (model: AddJackpotModel) => {
	const ModalForm = createModalForm(model);

	const AccountField = reflect({
		view: Select,
		bind: {
			value: model.form.fields.accountId.$value.map((value) => value?.toString() ?? null),
			onChange: (value) =>
				model.form.fields.accountId.change(value === null ? value : Number(value)),
			data: model.$accounts.map((accounts) =>
				accounts.map(({ id, name }) => ({
					value: id.toString(),
					label: name,
				})),
			),
			disabled: model.form.fields.accountId.$disabled,
			label: translated('add-jackpot.fields.account.label'),
			placeholder: translated('add-jackpot.fields.account.placeholder'),
			errorProps: { 'data-test-id': 'add-jackpot-account-select-error' },
			error: combine(
				[model.form.fields.accountId.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-jackpot.fields.account.label'),
					}),
			),
			searchable: true,
			required: true,
		},
	});

	const GameField = reflect({
		view: Select,
		bind: {
			'data-test-id': 'add-jackpot-game-select',
			label: translated('add-jackpot.fields.game.label'),
			placeholder: translated('add-jackpot.fields.game.placeholder'),
			required: true,
			disabled: model.form.fields.gameId.$disabled,
			data: model.$games,
			value: model.form.fields.gameId.$value.map((value) => value?.toString() ?? null),
			onChange: (value) => model.form.fields.gameId.change(value === null ? value : Number(value)),
			error: combine(
				[model.form.fields.gameId.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-jackpot.fields.game.label'),
					}),
			),
		},
	});

	const CurrencyField = reflect({
		view: Select,
		bind: {
			'data-test-id': 'add-jackpot-currency-select',
			required: true,
			label: translated('add-jackpot.fields.currency.label'),
			placeholder: translated('add-jackpot.fields.currency.placeholder'),
			data: model.$currencies,
			disabled: model.form.fields.currency.$disabled,
			value: model.form.fields.currency.$value,
			onChange: model.form.fields.currency.setValue,
			errorProps: { 'data-test-id': 'add-jackpot-currency-select-error' },
			error: combine(
				[model.form.fields.currency.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-jackpot.fields.currency.label'),
					}),
			),
		},
	});

	const TypeField = reflect({
		view: Select,
		bind: {
			'data-test-id': 'add-jackpot-type-select',
			label: translated('add-jackpot.fields.type.label'),
			placeholder: translated('add-jackpot.fields.type.placeholder'),
			required: true,
			data: Object.entries(JACKPOT_TYPES).map(([key, value]) => ({
				value: value.toString(),
				label: key.charAt(0).toUpperCase() + key.slice(1).toLowerCase(),
			})),
			value: model.form.fields.type.$value.map((value) => value?.toString() ?? null),
			onChange: (value) =>
				model.form.fields.type.change(value === null ? null : (Number(value) as JackpotType)),
		},
	});

	const BetRateField = reflect({
		view: NumberInput,
		bind: {
			required: true,
			decimalScale: 2,
			rightSection: '%',
			'data-test-id': 'add-jackpot-bet-rate-input',
			label: translated('add-jackpot.fields.rate.label'),
			placeholder: translated('add-jackpot.fields.rate.placeholder'),
			value: model.form.fields.betRate.$value,
			onChange: (value) => model.form.fields.betRate.change(String(value ?? '')),
			error: combine(
				[model.form.fields.betRate.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-jackpot.fields.rate.label'),
					}),
			),
		},
	});

	const InitialAmountField = reflect({
		view: NumberInput,
		bind: {
			'data-test-id': 'add-jackpot-initial-amount-input',
			label: translated('add-jackpot.fields.initial.label'),
			placeholder: translated('add-jackpot.fields.initial.placeholder'),
			value: model.form.fields.initialAmount.$value,
			decimalScale: 2,
			min: 0,
			rightSection: model.form.fields.currency.$value,
			error: combine(
				[model.form.fields.initialAmount.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-jackpot.fields.initial.label'),
					}),
			),
			trimLeadingZeroesOnBlur: false,
			onChange: (value) => model.form.fields.initialAmount.change(String(value)),
		},
	});

	const LimitAmountField = reflect({
		view: NumberInput,
		bind: {
			required: true,
			'data-test-id': 'add-jackpot-limit-amount-input',
			label: translated('add-jackpot.fields.limit.label'),
			placeholder: translated('add-jackpot.fields.limit.placeholder'),
			value: model.form.fields.limitAmount.$value,
			decimalScale: 2,
			rightSection: model.form.fields.currency.$value,
			onChange: (value) => model.form.fields.limitAmount.change(String(value ?? '')),
			error: combine(
				[model.form.fields.limitAmount.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-jackpot.fields.limit.label'),
						secondFieldName: t('add-jackpot.fields.initial.label'),
					}),
			),
		},
	});

	const AddJackpotModalButton = reflect({
		view: ModalForm.Button,
		bind: {
			'data-test-id': 'add-jackpot-button',
			leftSection: <Plus />,
			className: cls.addJackpotModalButton,
			disabled: viewerModel.$role.map((role) => role !== USER_ROLES.god),
			children: translated('add-jackpot.title'),
			modalData: null,
		},
	});

	const AddJackpotModal = () => {
		const { t } = useTranslation();

		const pending = useUnit(model.$pending);

		return (
			<ModalForm
				title={t('add-jackpot.title')}
				cancelButtonProps={{
					children: t('add-jackpot.cancel'),
				}}
				submitButtonProps={{
					children: t('add-jackpot.add'),
					loading: pending,
				}}
			>
				<AccountField />
				<GameField />
				<CurrencyField />
				<TypeField />
				<BetRateField />
				<InitialAmountField />
				<LimitAmountField />
			</ModalForm>
		);
	};

	return Object.assign(AddJackpotModal, {
		Button: AddJackpotModalButton,
	});
};
